/* eslint-disable max-len */
import { getLogger } from '@jitsi/logger';
import { findIndex, findLastIndex } from 'lodash';
import React from 'react';

import { openDialog } from '../../react/features/base/dialog/actions';
import { isMobileBrowser } from '../../react/features/base/environment/utils';
import i18next from '../../react/features/base/i18n/i18next';
import {
    JitsiConnectionEvents,
    JitsiTrackErrors,
    browser
} from '../../react/features/base/lib-jitsi-meet';
import { PARTICIPANT_ROLE } from '../../react/features/base/participants/constants';
import Platform from '../../react/features/base/react/Platform';
import { updateSettings } from '../../react/features/base/settings/actions';
import { toggleScreensharing } from '../../react/features/base/tracks/actions';
import { parseURLParams } from '../../react/features/base/util/parseURLParams';
import { beginAddPeople } from '../../react/features/invite/actions';
import { isPrejoinPageVisible } from '../../react/features/prejoin/functions';
import { iAmVisitor } from '../../react/features/visitors/functions';
import { BackButton, FullScreenButton } from '../../x-bees/base/Buttons';
import ToggleSubtitlesButton
    from '../../x-bees/base/Buttons/components/web/ToggleSubtitlesButton';
import { checkIfUserCanJoin, isWebinarEnabledInRoom } from '../../x-bees/components/webinar/functions';
import PiPButton from '../components/PiP/components/web/PictureInPictureButton';
import { RecordingsListButton } from '../components/RecordingsList';
import PopupRejoin from '../components/modals/PopupRejoin';
import { setPressureRecord } from '../components/pressure-observer/actions';
import { EndConferenceButton } from '../components/toolbox';
import { ShowButtonTutorial } from '../components/tutorial';
import {
    shouldShowUsabillaFeedback,
    showUsabillaFeedback
} from '../components/usabilla/functions.web';
import VisitorMuteButton
    from '../components/visitors/components/VisitorMuteButton';
import WhiteboardButton from '../components/whiteboard/components/web/WhiteboardButton';
import ControlSharingDialogListButton
    from '../components/wildix-remote-control/components/web/ControlSharingDialogListButton';
import { checkTurnServerAvailability } from '../functions.any';
import WildixEvents from '../service/WildixEvents';
import { UnpinButton } from '../wizyconf/components/Buttons';

import Env from './Environment';


declare var APP: Object;
declare var interfaceConfig: Object;


const logger = getLogger(__filename);

export default class WildixMethods {
    #pressureObserverState = {
        criticalStart: null,
        criticalDuration: 0
    };
    #disableShortcut = false;
    isConferenceLoaded = false;

    constructor() {
        document.addEventListener(WildixEvents.CONFERENCE_JOINED, () => {
            if (iAmVisitor(APP.store.getState)) {
                return;
            }
            this.handleAutostartScreenSharing();
            this.isConferenceLoaded = true;
        });

    }

    pressureObserverEvent(records) {
        const [ oneRecord ] = records;
        const plainObjectRecord = {
            source: oneRecord.source,
            state: oneRecord.state,
            time: oneRecord.time
        };

        if (this.isConferenceLoaded) {
            APP.store.dispatch(setPressureRecord(plainObjectRecord));
        }
    }
    handleAutostartScreenSharing() {
        const { startWithScreenSharing } = APP.store.getState()['features/base/config'];

        // start Screen Sharing dialog with 1 second delay to avoid race condition
        if (startWithScreenSharing) {
            setTimeout(() => {
                APP.store.dispatch(toggleScreensharing(true));
            }, 1000);
        }
    }

    /**
     *
     * @param onUnload
     */
    disposeConference = onUnload => {
        APP.wildix.eventEmitter.emit(WildixEvents.CONFERENCE_DISPOSE);
        logger.error('Close disposeConference', 'onUnload:', onUnload);


        APP.conference.askReloadPage = false;
    };

    /**
     *
     */
    conferenceLoaded = () => {
        APP.conference.needReload = false;
        this.#initEvents();
        this.muteReactions();
        if (!APP.isExternalProvider && !Env.isLocalHost()) {
            APP.conference.askReloadPage = true;
        }
    };

    muteReactions() {
        APP.store.dispatch(updateSettings({ soundsReactions: false }));
    }

    /**
     *
     * @returns {Promise<any> | Promise}
     */

    userAuth = () => new Promise((resolve, reject) => {
        const { iAmRecorder, isXbees, turnServerWebDomain } = APP.store.getState()['features/base/config'];
        const state = APP.store.getState();

        // Before joining, we are going to reach Turn server to make it more trusted for firewalls.
        // It must not be a blocked operation even if we can't reach it
        if (!iAmRecorder) {
            checkTurnServerAvailability(turnServerWebDomain)
                .catch(err => {
                    console.warn(err);
                });
        }

        if (iAmRecorder || (isXbees && !isWebinarEnabledInRoom(state))) {
            return resolve();
        } else if (isXbees && isWebinarEnabledInRoom(state)) {
            return checkIfUserCanJoin(state, APP.store.dispatch)
                .then(resolve)
                .catch(reject);
        }

        APP.wildixPBX.userAuthWithCredentials()
            .then(resolve)
            .catch(reject);

    });

    /**
     *
     */
    #initEvents = () => {
        APP.connection.addEventListener(JitsiConnectionEvents.CONNECTION_FAILED, () => {
            APP.conference.askReloadPage = false;
        });
    };

    /**
     *
     */
    showNotificationMacSharing = ({ name } = {}) => {
        if (Env.isMacintosh()) {
            const { urlArticleForAccessSharing: { screenRecording, recheckAccess } } = APP.store.getState()['features/base/config'];
            let urlFromConfig = screenRecording;

            if (name === JitsiTrackErrors.PERMISSION_DENIED) {
                urlFromConfig = recheckAccess;
            }

            APP.wildix.eventEmitter.emit(WildixEvents.SHOW_NOTIFICATION, {
                titleKey: 'notification.screenSharingNotHaveAccess',
                description: React.createElement('a', {
                    href: urlFromConfig,
                    target: '_blank',
                    key: 0
                }, 'https://wildix.atlassian.net/wiki')
            }, 10000);
        }
    };

    /**
     *
     */
    disableShortcut = state => {
        if (state) {
            this.#disableShortcut = state;
        }

        return this.#disableShortcut;
    };

    /**
     *
     */
    hangupConference = () => {
        const state = APP.store.getState();

        if (shouldShowUsabillaFeedback(state)) {
            showUsabillaFeedback(state);
        }

        const { isXbees } = state['features/base/config'];

        if (isXbees) {
            APP.xBeesEvents.destroyIframe();

            return;
        }

        APP.wildix.eventEmitter.emit(WildixEvents.CONFERENCE_HUNGUP);
        APP.store.dispatch(openDialog(PopupRejoin));


        // Disable handle keydown event
        this.disableShortcut(true);
        const elementLargeVideo = document.getElementById('largeVideoContainer');

        if (elementLargeVideo) {
            elementLargeVideo.innerHTML = '';
        }
    };

    /**
     *
     * @returns {string}
     */
    conferenceRole = role => {
        const { conferenceOwner, pbxUser, pbxSerial } = APP.store.getState()['features/base/config'];

        if (pbxUser && !role) {
            return conferenceOwner.split('@')[1] === pbxSerial ? PARTICIPANT_ROLE.MODERATOR : PARTICIPANT_ROLE.PARTICIPANT;
        }

        return role === PARTICIPANT_ROLE.MODERATOR ? PARTICIPANT_ROLE.MODERATOR : PARTICIPANT_ROLE.PARTICIPANT;
    }

    /**
     *
     * @param key
     * @param options
     * @returns {*}
     */
    translate = (key, options) => i18next.t(key, options ? options : undefined);

    /**
     *
     * @returns {string}
     */
    generateDeepLinkingURL = () => {
        // If the user installed the app while this Component was displayed
        // (e.g. the user clicked the Download the App button), then we would
        // like to open the current URL in the mobile app. The only way to do it
        // appears to be a link with an app-specific scheme, not a Universal
        // Link.

        const appScheme = interfaceConfig.APP_SCHEME || 'com.wizyconf';
        const { roomToken } = APP.store.getState()['features/base/config'];

        // Android/iOS: Replace the protocol part with the app scheme.
        return `${appScheme}://join?conferenceId=${roomToken}`;
    };

    showInviteDialog() {
        APP.store.dispatch(beginAddPeople());
    }

    get getUrlForInviteDialog() {
        const { baseUrl, roomToken, xBeesWebDomain } = APP.store.getState()['features/base/config'];

        if (APP.isExternalProvider) {
            const xBeesChannelId = APP.xBeesMethods.getXBeesChannelId();

            return `${xBeesWebDomain}/inbox/${xBeesChannelId}?join`;
        }

        return `${baseUrl}conf/${roomToken}`;
    }

    /**
     *
     * @returns {string}
     */
    // eslint-disable-next-line no-confusing-arrow
    generateDownloadURL = () => Platform.OS === 'android'
        ? 'https://play.google.com/store/apps/details?id=com.wizyconf'
        : 'https://apps.apple.com/ru/app/wizyconf/id1510379804?l=en';

    updateConfigFromUrl = () => {
        const objectParams = parseURLParams(window.location, true, 'search');

        window.config = Object.assign(window.config, objectParams);
    };

    overrideInterfaceConfig(params) {
        window.interfaceConfig = Object.assign(window.interfaceConfig, params);
    }

    get isWizyconf() {
        return Env.isWizyconf();
    }

    getClassFromLabel({ accessibilityLabel, buttonKey }) {
        if (typeof accessibilityLabel !== 'string') {
            return ` ${buttonKey}`;
        }
        const splitString = accessibilityLabel.split('.');
        const lastString = splitString[splitString.length - 1];

        return ` ${lastString?.toLowerCase()} ${buttonKey}`;
    }

    objectCustomButtons = {
        mainMenuButtonsLength: 0,
        overflowMenuButtonsLength: 0
    };
    customButtons({ mainMenuButtons, overflowMenuButtons }) {
        const { mainMenuButtonsLength, overflowMenuButtonsLength } = this.objectCustomButtons;

        if (mainMenuButtonsLength === mainMenuButtons.length
            && overflowMenuButtonsLength === overflowMenuButtons.length) {
            return {
                ...this.objectCustomButtons
            };
        }
        const state = APP.store.getState;

        if (isPrejoinPageVisible(state())) {
            return {
                mainMenuButtons,
                overflowMenuButtons
            };
        }

        this.objectCustomButtons = {
            mainMenuButtonsLength: mainMenuButtons.length,
            overflowMenuButtonsLength: overflowMenuButtons.length
        };
        const listComponentsButtons = [
            {
                key: 'visitorMuteButton',
                Content: VisitorMuteButton,
                group: 0,
                visible: true,
                location: 'main'
            },
            {
                key: 'recordinglist',
                Content: RecordingsListButton,
                group: 2,
                visible: !this.isWizyconf && !APP.isExternalProvider,
                location: 'overflow'
            },
            {
                key: 'fullscreenxbees',
                Content: FullScreenButton,
                group: 2,
                visible: APP.isExternalProvider,
                location: 'main'
            },
            {
                key: 'buttonBackXbees',
                Content: BackButton,
                group: 2,
                visible: APP.isExternalProvider,
                location: 'main'
            },
            {
                key: 'wizyconf-whiteboard',
                Content: WhiteboardButton,
                group: this.isWizyconf ? 2 : 3,
                visible: true,
                location: this.isWizyconf ? 'main' : 'overflow'
            },
            {
                key: 'tutorial',
                Content: ShowButtonTutorial,
                group: 2,
                visible: true,
                location: this.isWizyconf ? 'main' : 'overflow'
            },
            {
                key: 'endConference',
                Content: EndConferenceButton,
                group: 'end',
                visible: !this.isWizyconf,
                location: 'overflow'
            },
            {
                key: 'pipButton',
                Content: PiPButton,
                group: 2,
                visible: (browser.isChromiumBased() || browser.isElectron()) && !isMobileBrowser() && !this.isWizyconf,
                location: 'overflow'
            },
            {
                key: 'controlDialogList',
                Content: ControlSharingDialogListButton,
                group: 2,
                visible: (browser.isChromiumBased() || browser.isSafari() || browser.isElectron()) && !isMobileBrowser() && !this.isWizyconf,
                location: 'overflow'
            },
            {
                key: 'toggleSubtitlesButton',
                nearKey: 'closedcaptions',
                Content: ToggleSubtitlesButton,
                group: 2,
                visible: APP.isExternalProvider,
                location: 'overflow'
            },

            // Always keep in the end of array
            {
                key: 'unpin',
                Content: UnpinButton,
                group: 2,
                visible: this.isWizyconf,
                location: 'main'
            }
        ];
        const buttonsToRemove = [
            {
                key: 'whiteboard',
                condition: true
            },
            {
                key: 'noisesuppression',
                condition: true
            },
            {
                key: 'shortcuts',
                condition: true
            },
            {
                key: 'videoquality',
                condition: true
            }
        ];

        buttonsToRemove.forEach(item => {
            if (item.condition) {
                const indexMainMenuButtons = findIndex(mainMenuButtons, element => element.key === item.key, 0);

                if (indexMainMenuButtons !== -1) {
                    mainMenuButtons.splice(indexMainMenuButtons, 1);
                } else {
                    // check the OverflowButtons array only in case we didn't find it in the MainButtons
                    const indexOverflowMenuButtons = findIndex(overflowMenuButtons, element => element.key === item.key, 0);

                    if (indexOverflowMenuButtons !== -1) {
                        overflowMenuButtons.splice(indexOverflowMenuButtons, 1);
                    }
                }
            }
        });
        listComponentsButtons.forEach(item => {
            if (item.visible && item.location === 'main') {
                if (item.group === 'end') {
                    mainMenuButtons.push(item);
                } else {
                    const indexToAppend = findLastIndex(mainMenuButtons, newIndex => newIndex.group === item.group);

                    mainMenuButtons.splice(indexToAppend + 1, 0, item);
                }
            } else if (item.visible && item.location === 'overflow') {
                if (item.group === 'end') {
                    overflowMenuButtons.push(item);
                } else if (item.nearKey) {
                    const indexToAppend = overflowMenuButtons.findIndex(nameKey => nameKey.key === item.nearKey);

                    overflowMenuButtons.splice(indexToAppend, 0, item);
                } else {
                    const indexToAppend = findLastIndex(overflowMenuButtons, newIndex => newIndex.group === item.group);

                    overflowMenuButtons.splice(indexToAppend + 1, 0, item);
                }
            }
        });

        this.objectCustomButtons = {
            ...this.objectCustomButtons,
            mainMenuButtons,
            overflowMenuButtons
        };

        if (iAmVisitor(state)) {
            this.objectCustomButtons.overflowMenuButtons = [];
        }

        return { ...this.objectCustomButtons };
    }

    getRoomData() {
        const { pbxSerial = null } = APP.store.getState()['features/base/config'];
        const { subject } = APP.store.getState()['features/base/conference'];

        const roomSubject = typeof subject === 'string' ? subject.replace(/[^\s\w\d-_]*/g, '').trim() : '';

        return {
            pbxSerial,
            roomSubject
        };
    }
}
